import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Typography
} from "@material-ui/core";
import { CheckCircle, Refresh } from "@material-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import {
  isEmailCodeAnyAddressEnabled,
  isInstitutionalLoginEnabled
} from "../../components/institutional/institutional-flags";
import { isTokenUsable } from "../../utils/session";
import { STALE_SESSION_KEYS } from "../../utils/stale-session";
import {
  QUIZ_LINK_OPAQUE_PATH,
  resolveQuizLinkToken,
  toOpaqueQuizLinkPath
} from "../../utils/url-secret-scrub";
import { api } from "../../services/apiService";
import "./notes-to-quiz-claim.scss";

// Wording for the guided claim step (A1 contract, Amendments 4 and 5, after
// two UX reviews), kept together here rather than inline in the markup.
const PANEL_BRAND = "Medlearnity · QLearn";
const LOADING_HEADING = "Opening your quiz";
const LOADING_BODY = "One moment.";

const questionCountLabel = (count) => (count === 1 ? "1 question" : `${count} questions`);
const QUIZ_WITHOUT_COUNT = "A quiz";
const signInHeading = (count, tutor) => {
  const what = count ? questionCountLabel(count) : QUIZ_WITHOUT_COUNT;
  return tutor
    ? `${what} from your session with ${tutor}`
    : `${what} from your tutoring session`;
};
const SIGN_IN_HEADING_CLAIMED = "Your quiz is saved";
const SIGN_IN_ADDRESS_LABEL = "Sign in with this address";
const SIGN_IN_WITHOUT_HINT = "Sign in with the address your quiz email was sent to.";
// What a screen reader says for a masked address such as jd•••@med.example.edu:
// the visible characters spelled out, not a run of dots.
const maskedAddressSpoken = (visible, domain) =>
  [visible.split("").join(" "), "hidden characters", `at ${domain}`].filter(Boolean).join(", ");
// What to do on the login page, by kind of address. The sign-in panel shows
// the address just above the line, so it says "this address".
const SIGN_IN_NEXT_STEP = {
  google: "On the next screen, tap Sign In with Google and choose this address.",
  school: "On the next screen, enter this address under Institutional Email. We'll email you a code.",
  other: "On the next screen, use Sign In with Google if this is a Google account. If not, choose Phone Sign In and enter this address when it asks for your email.",
  // Any-address email-code flag on: the tab is "Email Code" and takes any address.
  emailCode: "On the next screen, enter this address under Email Code. We'll email you a code.",
  // A non-school, non-Gmail address may belong to an account that signs in with Google
  // or phone, which the code would only refuse; say so before a code is spent.
  emailCodeOther: "On the next screen, enter this address under Email Code. We'll email you a code. Already use QLearn with Google or your phone? Sign in that way instead."
};
const SIGN_IN_BUTTON = "Sign in to start";
const SIGN_IN_BUTTON_CLAIMED = "Sign in to reopen";
const SIGN_IN_FOOTNOTE = "QLearn is Medlearnity's question bank. No account yet? Signing in creates one. No password needed.";
// A new account could not reopen a quiz another account already holds.
const SIGN_IN_FOOTNOTE_CLAIMED = "QLearn is Medlearnity's question bank. Sign in the same way you did the first time.";

const WRONG_ADDRESS_HEADING = "This quiz was sent to a different email";
// With a hint the sentence is this lead, the masked address, then a full stop.
const WRONG_ADDRESS_WITH_HINT_LEAD = "You're signed in with a different address. This quiz was sent to ";
const WRONG_ADDRESS_WITHOUT_HINT = "You're signed in with a different address from the one your quiz email was sent to.";
// Here the address was only named in a sentence about the account they are
// in, where "this address" would read as that account: "that address".
const WRONG_ADDRESS_NEXT_STEP = {
  google: "On the next screen, tap Sign In with Google and choose that address.",
  school: "On the next screen, enter that address under Institutional Email. We'll email you a code.",
  other: "On the next screen, use Sign In with Google if that is a Google account. If not, choose Phone Sign In and enter that address when it asks for your email.",
  emailCode: "On the next screen, enter that address under Email Code. We'll email you a code.",
  // The wrong-address panel keeps the plain line: its reader is already signed in.
  emailCodeOther: "On the next screen, enter that address under Email Code. We'll email you a code."
};
const WRONG_ADDRESS_BUTTON = "Sign in with that address";
const WRONG_ADDRESS_BUTTON_NO_HINT = "Sign in with a different address";
const WRONG_ADDRESS_BUTTON_NOTE = "This signs you out of your current account on this device.";
// Google skips its account chooser when the browser holds one Google session,
// which is exactly the student who just signed in with the wrong one.
const WRONG_ADDRESS_GOOGLE_NOTE = "If Google signs you back in without asking which account, sign in to that address at accounts.google.com first, then open your quiz link again.";
const WRONG_ADDRESS_SECONDARY = "Practice other questions";
const HELP_LINE_LEAD = "Can't sign in with that address? Email ";
const SUPPORT_EMAIL = "support@medlearnity.com";

// The backend's placeholder title; printing it tells the student nothing.
const DEFAULT_QUIZ_TITLE = "Review Quiz";

// The longest the page waits for the open ping's link context, counted from
// when the ping starts. Past it the page carries on as if there were none.
const LINK_CONTEXT_WAIT_MS = 2500;

// The claim API's wording when a link belongs to another address: the
// fallback signal for a refusal that arrives without a link context.
const WRONG_ADDRESS_REFUSAL_FRAGMENT = "another email address";

const GOOGLE_MAIL_DOMAINS = new Set(["gmail.com", "googlemail.com"]);
const SCHOOL_DOMAIN_SUFFIXES = [".edu", ".org"];

// `auth` is the login page's own tab selector (getInitialAuthMethod there);
// without it the page opens on its default tab, as it always has. The return
// address is always the token-free claim path: the sign-in page's address is
// read by the same tags as this one, and it comes back here to the tab stash.
const loginRouteFor = (auth) => {
  const next = `next=${encodeURIComponent(QUIZ_LINK_OPAQUE_PATH)}`;
  return auth ? `/login?auth=${auth}&${next}` : `/login?${next}`;
};

const getErrorMessage = (response) => {
  if (!response) {
    return "We could not prepare this quiz link.";
  }
  if (response.errMsg?.message) {
    return response.errMsg.message;
  }
  if (response.errMsg?.error) {
    return response.errMsg.error;
  }
  if (response.message) {
    return response.message;
  }
  return "This quiz link is unavailable, expired, already used, or assigned to another email address.";
};


// What the server itself said about a refused claim, if anything. A network
// failure has none, and getErrorMessage then prints its own catch-all.
const serverMessages = (response) =>
  [response?.errMsg?.message, response?.errMsg?.error, response?.message].filter(
    (text) => typeof text === "string" && text.trim() !== ""
  );

// Matched against what the server sent only: getErrorMessage's catch-all
// fallback mentions the same words and must not read as a wrong-address
// refusal.
const isWrongAddressRefusal = (response) =>
  serverMessages(response).some((text) => text.includes(WRONG_ADDRESS_REFUSAL_FRAGMENT));

// Whether reloading could change the outcome. Once the server has given its
// reason (expired, revoked, already claimed) it cannot, and Retry is only a
// loud button that fails again. It can after a failure with no server message
// (network), after a server error, and after a 401: there the stale-session
// guard may have just cleared a login the server no longer accepts, without
// leaving this page, and a reload is what moves the student onto the sign-in
// path (see PUBLIC_ENTRY_PREFIXES in utils/stale-session.js).
const isRetryWorthwhile = (response) => {
  const status = Number(response?.errStatus);
  return serverMessages(response).length === 0 || status >= 500 || status === 401;
};

// A mocked or older-backend ping result carries no `context` field at all;
// that, like an explicit null, means there is no link context.
const readLinkContext = (result) => {
  const context = result?.context;
  return context !== null && typeof context === "object" ? context : null;
};

const offersSignIn = (context) =>
  context?.state === "READY" || context?.state === "CLAIMED";

const readText = (value) =>
  (typeof value === "string" && value.trim() !== "" ? value.trim() : null);

const readCount = (value) => (Number.isInteger(value) && value > 0 ? value : null);

const readQuizTitle = (value) => {
  const title = readText(value);
  return title && title.toLowerCase() !== DEFAULT_QUIZ_TITLE.toLowerCase() ? title : null;
};

// A plain scan, not a regular expression: the hint comes from the server, and
// /\.+$/ backtracks quadratically on a long run of dots that does not end
// the string.
const withoutTrailingDots = (text) => {
  let end = text.length;
  while (end > 0 && text[end - 1] === ".") end -= 1;
  return text.slice(0, end);
};

// The server's masked hint split at its last "@", or null when it is not
// shaped like local@domain. The page only ever handles this masked form: it
// never sees, rebuilds or prints the full address. `domain` is normalised for
// matching (case, a trailing dot); `local` and `shownDomain` are as sent.
const splitHint = (hint) => {
  const at = hint ? hint.lastIndexOf("@") : -1;
  if (at < 0) return null;
  const shownDomain = hint.slice(at + 1);
  const domain = withoutTrailingDots(shownDomain.trim().toLowerCase());
  if (!domain) return null;
  return { local: hint.slice(0, at), shownDomain, domain };
};

// The login page shows a Google button and tabs, not an email field, so the
// hint's domain decides both what to tell the student to do there (`kind`
// picks each panel's own wording) and which tab to open for them (`auth`).
// No hint, or a malformed one: say nothing, open the default tab.
const loginGuidanceFor = (hint) => {
  const domain = splitHint(hint)?.domain;
  if (!domain) return { kind: null, auth: null };
  if (GOOGLE_MAIL_DOMAINS.has(domain)) {
    return { kind: "google", auth: "google" };
  }
  const school = SCHOOL_DOMAIN_SUFFIXES.some((suffix) => domain.endsWith(suffix));
  // With the any-address flag the same tab is "Email Code" and takes every other
  // address too (Yahoo, Outlook, work), so nobody is sent to phone sign-in to type
  // an address they could prove directly. School links keep their own alias.
  if (isEmailCodeAnyAddressEnabled()) {
    return school
      ? { kind: "emailCode", auth: "institutional" }
      : { kind: "emailCodeOther", auth: "email" };
  }
  // The Institutional Email tab exists only while its build flag is on;
  // without it a school address signs in like any other.
  if (isInstitutionalLoginEnabled() && school) {
    return { kind: "school", auth: "institutional" };
  }
  return { kind: "other", auth: null };
};

// The masked address as text. It may wrap in one place, after the "@", so a
// long school domain moves to its own line whole instead of being cut. A
// well-formed hint is read out as its visible characters; role="img" is what
// makes that label valid on plain text, so it is only set alongside one.
const MaskedAddress = ({ hint }) => {
  const parts = splitHint(hint);
  if (!parts) return hint;
  // split/join rather than String.replaceAll: the build ships no polyfills
  // and still targets browsers that lack it, and this runs during render.
  const visible = parts.local.split("•").join("");
  return (
    <span
      role="img"
      aria-label={maskedAddressSpoken(visible, parts.domain)}
    >
      {parts.local}@<wbr />{parts.shownDomain}
    </span>
  );
};

// Fires the open ping once and resolves the link context it answers with, or
// null when there is none: an older backend or the rate limiter (204), a ping
// that rejects or throws, or no answer within LINK_CONTEXT_WAIT_MS. Never
// rejects. The request itself still starts a microtask later, so the caller's
// synchronous stale-session clear lands first and a signed-out ping goes out
// without a token.
const openQuizLink = (token) => {
  let capTimer;
  const cap = new Promise((resolve) => {
    capTimer = setTimeout(() => resolve(null), LINK_CONTEXT_WAIT_MS);
  });
  const ping = Promise.resolve()
    .then(() => api.trackNotesToQuizOpen(token))
    .then(readLinkContext)
    .catch(() => null);
  const context = Promise.race([ping, cap]).then((value) => {
    clearTimeout(capTimer);
    return value;
  });
  return { context, cancel: () => clearTimeout(capTimer) };
};

// The claim response may carry an optional mentorship consent prompt when the
// ops quiz-generation flow resolved a tutor for this student (design:
// docs/qlearn-mentorship-quizgen-autorequest.md §2 + qlearn-unified PR #122).
// Only a TUTOR-initiated PENDING (or a mutual-activate ACTIVE confirmation) is
// ever sent — a student cannot accept their own request. Anything else, or an
// old backend, omits the field and the claim page behaves byte-identically.
const readMentorshipPrompt = (response) => {
  const prompt = response?.mentorshipPrompt;
  if (!prompt?.linkId) return null;
  return prompt;
};

const tutorName = (prompt) => prompt?.tutorDisplayName || "Your tutor";

// The claim token, kept out of the address. GTM (GA4 with it) and the Meta
// pixel read the page address and GTM reports every history change, so the
// token must never be in it while they run. The app shell normally moved it
// into the tab stash before they started and left this page at
// /notes-to-quiz/claim (utils/url-secret-scrub). If this page is ever reached
// with the token still in the address (the shell could not store it, and so
// held both tags for that document), it is remembered and taken out here.
// Call this hook first: its effect then runs ahead of the page's own effects,
// so the token is remembered before the page can leave for sign-in.
const useQuizLinkToken = () => {
  const { token: routeSegment } = useParams();
  const history = useHistory();

  useEffect(() => {
    const here = history.location;
    const tokenFreePath = toOpaqueQuizLinkPath(here.pathname);
    if (tokenFreePath !== here.pathname) {
      history.replace({ pathname: tokenFreePath, search: here.search, hash: here.hash });
    }
  }, [history, routeSegment]);

  return useMemo(() => resolveQuizLinkToken(routeSegment), [routeSegment]);
};

export default function NotesToQuizClaimPage() {
  const token = useQuizLinkToken();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  // Off only once the server has given a refusal a reload cannot change.
  const [retryOffered, setRetryOffered] = useState(true);
  // Set only when the claim succeeds AND carries a mentorship prompt; holding
  // the student on the claim page to show the consent card before the quiz.
  const [quizRoute, setQuizRoute] = useState("");
  const [mentorshipPrompt, setMentorshipPrompt] = useState(null);
  // "pending" (actionable Accept / Not now) | "linked" (confirmation, no
  // buttons) | "dismissed" (Not now — stays PENDING in My Tutors).
  const [cardState, setCardState] = useState("pending");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");
  // The open ping's link context, set only for a signed-out visitor whose
  // link is READY or CLAIMED: shows the sign-in panel instead of redirecting.
  const [signInContext, setSignInContext] = useState(null);
  // { hint } once a claim is refused because the signed-in account is not the
  // link's address; hint is the server's masked address, or null.
  const [wrongAddress, setWrongAddress] = useState(null);

  useEffect(() => {
    if (!token) {
      // The token-free address opened where the link itself never was (a new
      // tab, another browser, a bookmark): only the emailed link carries it,
      // so a reload cannot help and Retry is not offered.
      setLoading(false);
      setErrorMessage("Open the quiz link in your email again to continue.");
      setRetryOffered(false);
      return;
    }

    let isActive = true;
    const linkOpen = openQuizLink(token);
    const deactivate = () => {
      isActive = false;
      linkOpen.cancel();
    };
    // Not an expected path. It is for a handler below that throws while
    // navigating or rendering what it set (Safari, for one, throws from
    // history.replaceState when it is called too often), and for a claim
    // call that rejects outright.
    const showUnexpectedFailure = () => {
      if (!isActive) return;
      setErrorMessage("We could not prepare this quiz link.");
      setLoading(false);
    };

    const authToken = window.localStorage.getItem("token");
    if (!isTokenUsable(authToken)) {
      // Clear the WHOLE stale session identity, not just the token: a
      // surviving userId made the login page auto-redirect right back here
      // (gate leg-2 B1 infinite loop).
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("userId");
      // A link the server vouches for gets the sign-in panel. Anything else
      // (older backend, unavailable link, slow or failed ping) goes to login
      // exactly as before, one round trip later.
      linkOpen.context
        .then((context) => {
          if (!isActive) return;
          if (offersSignIn(context)) {
            setSignInContext(context);
            setLoading(false);
            return;
          }
          history.replace(loginRouteFor());
        })
        .catch(showUnexpectedFailure);
      return deactivate;
    }

    setLoading(true);
    setErrorMessage("");
    setRetryOffered(true);

    api.claimNotesToQuiz(token)
      .then((response) => {
        if (!isActive) return;

        if (response?.quizId && response?.quizRoute) {
          const prompt = readMentorshipPrompt(response);
          if (prompt) {
            // Hold on the claim page so the student sees the consent card
            // before entering the quiz; the quiz is one tap away either way.
            setQuizRoute(response.quizRoute);
            setMentorshipPrompt(prompt);
            setCardState(prompt.status === "ACTIVE" ? "linked" : "pending");
            setLoading(false);
            return;
          }
          history.replace(response.quizRoute);
          return;
        }

        // Only a refused claim waits on the open ping (the claim itself never
        // does), and only until the ping's own cap: the context says whether
        // this account is simply the wrong one for the link.
        return linkOpen.context.then((context) => {
          if (!isActive) return;
          if (context?.viewer === "MISMATCH" || isWrongAddressRefusal(response)) {
            setWrongAddress({ hint: readText(context?.recipientHint) });
          } else {
            setErrorMessage(getErrorMessage(response));
            setRetryOffered(isRetryWorthwhile(response));
          }
          setLoading(false);
        });
      })
      .catch(showUnexpectedFailure);

    return deactivate;
  }, [history, token]);

  // A guided panel replaces the spinner with nothing announcing it; moving
  // focus to its heading lets a screen reader read what the page now says.
  const guideHeadingRef = useRef(null);
  const guideShown = Boolean(signInContext || wrongAddress);
  useEffect(() => {
    if (guideShown) guideHeadingRef.current?.focus();
  }, [guideShown]);

  const retryClaim = () => {
    window.location.reload();
  };

  // Both ways out to login below are document loads, never route changes. The
  // login page sends anyone the Redux store still holds as signed in straight
  // back to `next`; from here that is a loop, and only a fresh document
  // empties that in-memory session. (The automatic no-context redirect above
  // deliberately stays the route replace it has always been.)
  const goToSignIn = (auth) => {
    // assign, so Back returns to this panel and the address it names.
    window.location.assign(loginRouteFor(auth));
  };

  const switchAccount = (auth) => {
    STALE_SESSION_KEYS.forEach((key) => window.localStorage.removeItem(key));
    // replace, so Back does not land on this refusal again.
    window.location.replace(loginRouteFor(auth));
  };

  const practiceOtherQuestions = () => {
    history.push("/create-quiz");
  };

  const startQuiz = () => {
    if (quizRoute) history.replace(quizRoute);
  };

  const acceptMentorship = async () => {
    if (!mentorshipPrompt?.linkId || accepting) return;
    setAccepting(true);
    setAcceptError("");
    try {
      // respondToMentorship RESOLVES {errStatus, errMsg} on failure (it never
      // rejects), so inspect the value rather than relying on catch. Success is
      // the updated ACTIVE DTO. Accept hits the existing PUT /mentorships/{id}/accept.
      const res = await api.respondToMentorship(mentorshipPrompt.linkId, "accept");
      if (res?.errStatus || res?.errMsg) {
        setAcceptError("We couldn't accept that just now. You can accept it anytime in My Tutors.");
      } else {
        setCardState("linked");
      }
    } catch (e) {
      setAcceptError("We couldn't accept that just now. You can accept it anytime in My Tutors.");
    } finally {
      setAccepting(false);
    }
  };

  const dismissMentorship = () => {
    // "Not now" just dismisses — the link stays PENDING and the student can
    // accept or decline it later from My Tutors (per PR #122 §2).
    setCardState("dismissed");
    setAcceptError("");
  };

  if (mentorshipPrompt) {
    const linked = cardState === "linked";
    return (
      <Box component="main" className="notes-to-quiz-page">
        <Paper elevation={0} className="notes-to-quiz-panel">
          <Typography component="h1">Your quiz is ready</Typography>
          <Typography component="p">Accept your tutor's request below, or jump straight in.</Typography>

          {cardState !== "dismissed" && (
            <Box component="section" className="notes-to-quiz-mentorship" data-testid="n2q-mentorship-card">
              {linked ? (
                <>
                  <div className="notes-to-quiz-mentorship-head">
                    <CheckCircle className="notes-to-quiz-mentorship-icon" fontSize="small" />
                    <Typography component="h2" data-testid="n2q-mentorship-linked">
                      You're connected with {tutorName(mentorshipPrompt)}
                    </Typography>
                  </div>
                  <Typography component="p">
                    They can now follow your quiz progress and the questions you tag. You can
                    stop sharing anytime in My Tutors.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="h2">
                    {tutorName(mentorshipPrompt)} would like to follow your QLearn progress
                  </Typography>
                  <Typography component="p">
                    Accepting lets your tutor see your quiz history and the questions you tag,
                    so they can plan your next session. You can stop sharing anytime in My Tutors.
                  </Typography>
                  {acceptError && (
                    <Typography component="p" className="notes-to-quiz-mentorship-error" data-testid="n2q-mentorship-error">
                      {acceptError}
                    </Typography>
                  )}
                  <div className="notes-to-quiz-mentorship-actions">
                    <Button
                      color="primary"
                      variant="contained"
                      size="small"
                      disabled={accepting}
                      onClick={acceptMentorship}
                      data-testid="n2q-mentorship-accept"
                    >
                      {accepting ? <CircularProgress size={18} color="inherit" /> : "Accept"}
                    </Button>
                    <Button
                      color="primary"
                      variant="text"
                      size="small"
                      disabled={accepting}
                      onClick={dismissMentorship}
                      data-testid="n2q-mentorship-decline"
                    >
                      Not now
                    </Button>
                  </div>
                </>
              )}
            </Box>
          )}

          <Box component="div" className="notes-to-quiz-actions">
            <Button
              color="primary"
              variant={cardState === "pending" ? "outlined" : "contained"}
              onClick={startQuiz}
              data-testid="n2q-start-quiz"
            >
              Start quiz
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (signInContext) {
    const claimed = signInContext.state === "CLAIMED";
    const quizTitle = readQuizTitle(signInContext.quizTitle);
    // Server-masked; the page never sees or derives the full address.
    const hint = readText(signInContext.recipientHint);
    // A claimed link was opened by signing in some way already; the domain
    // cannot say which, so it gets no guess and the login page's default tab.
    const { kind, auth } = claimed ? { kind: null, auth: null } : loginGuidanceFor(hint);
    const nextStep = kind && SIGN_IN_NEXT_STEP[kind];
    return (
      <Box component="main" className="notes-to-quiz-page">
        <Paper
          elevation={0}
          className="notes-to-quiz-panel notes-to-quiz-guide"
          data-testid="n2q-signin-panel"
        >
          <Box component="div" className="notes-to-quiz-headline">
            <Typography component="p" className="notes-to-quiz-brand">{PANEL_BRAND}</Typography>
            <Typography component="h1" ref={guideHeadingRef} tabIndex={-1}>
              {claimed
                ? SIGN_IN_HEADING_CLAIMED
                : signInHeading(
                  readCount(signInContext.questionCount),
                  readText(signInContext.tutorDisplayName)
                )}
            </Typography>
            {quizTitle && (
              <Typography component="p" className="notes-to-quiz-quiz-title">
                {quizTitle}
              </Typography>
            )}
          </Box>
          {hint ? (
            <Box component="div" className="notes-to-quiz-address">
              <Typography component="p">{SIGN_IN_ADDRESS_LABEL}</Typography>
              <Typography component="p" className="notes-to-quiz-address-hint">
                <MaskedAddress hint={hint} />
              </Typography>
            </Box>
          ) : (
            <Typography component="p">{SIGN_IN_WITHOUT_HINT}</Typography>
          )}
          {nextStep && (
            <Typography component="p" data-testid="n2q-next-step">{nextStep}</Typography>
          )}
          <Box component="div" className="notes-to-quiz-actions">
            <Button
              color="primary"
              variant="contained"
              onClick={() => goToSignIn(auth)}
              data-testid="n2q-signin-start"
            >
              {claimed ? SIGN_IN_BUTTON_CLAIMED : SIGN_IN_BUTTON}
            </Button>
          </Box>
          <Typography component="p" className="notes-to-quiz-footnote">
            {claimed ? SIGN_IN_FOOTNOTE_CLAIMED : SIGN_IN_FOOTNOTE}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (wrongAddress) {
    const { hint } = wrongAddress;
    const { kind, auth } = loginGuidanceFor(hint);
    const nextStep = kind && WRONG_ADDRESS_NEXT_STEP[kind];
    // A school address signs in by emailed code, never through Google; so does every
    // non-Gmail address while the email-code tab takes any address.
    const mayGoThroughGoogle = kind === "google" || kind === "other";
    return (
      <Box component="main" className="notes-to-quiz-page">
        <Paper
          elevation={0}
          className="notes-to-quiz-panel notes-to-quiz-guide"
          data-testid="n2q-wrong-address"
        >
          <Typography component="h1" ref={guideHeadingRef} tabIndex={-1}>
            {WRONG_ADDRESS_HEADING}
          </Typography>
          {hint ? (
            <Typography component="p">
              {WRONG_ADDRESS_WITH_HINT_LEAD}
              <MaskedAddress hint={hint} />
              .
            </Typography>
          ) : (
            <Typography component="p">{WRONG_ADDRESS_WITHOUT_HINT}</Typography>
          )}
          {nextStep && (
            <Typography component="p" data-testid="n2q-next-step">{nextStep}</Typography>
          )}
          <Box component="div" className="notes-to-quiz-actions">
            <Button
              color="primary"
              variant="contained"
              onClick={() => switchAccount(auth)}
              data-testid="n2q-switch-account"
            >
              {hint ? WRONG_ADDRESS_BUTTON : WRONG_ADDRESS_BUTTON_NO_HINT}
            </Button>
            <Typography component="p" className="notes-to-quiz-footnote">
              {WRONG_ADDRESS_BUTTON_NOTE}
            </Typography>
            {mayGoThroughGoogle && (
              <Typography component="p" className="notes-to-quiz-footnote">
                {WRONG_ADDRESS_GOOGLE_NOTE}
              </Typography>
            )}
            <Button
              color="primary"
              variant="text"
              onClick={practiceOtherQuestions}
              data-testid="n2q-practice-other"
            >
              {WRONG_ADDRESS_SECONDARY}
            </Button>
          </Box>
          <Typography component="p" className="notes-to-quiz-footnote">
            {HELP_LINE_LEAD}
            <Link href={`mailto:${SUPPORT_EMAIL}`} underline="always">{SUPPORT_EMAIL}</Link>
            .
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box component="main" className="notes-to-quiz-page">
      <Paper elevation={0} className="notes-to-quiz-panel">
        {loading ? (
          <>
            <CircularProgress color="primary" />
            <Typography component="h1">{LOADING_HEADING}</Typography>
            <Typography component="p">{LOADING_BODY}</Typography>
          </>
        ) : (
          <>
            <Typography component="h1">Quiz link unavailable</Typography>
            <Typography component="p">{errorMessage}</Typography>
            <Box component="div" className="notes-to-quiz-actions">
              <Button
                color="primary"
                variant="contained"
                onClick={() => history.push("/create-quiz")}
              >
                Create quiz
              </Button>
              {retryOffered && (
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={retryClaim}
                >
                  Retry
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
